import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';
import { useAuth } from '../context/AuthContext';
import './AdminMappings.css';

const APFS_POLICY_TYPES = [
  'MOTOR_INSURANCE',
  'COMPULSORY_MOTOR_INSURANCE',
  'MOTOR_ADD_ON',
  'MICROPLUS',
  'PET',
  'USE_CAR_WARRANTY'
];

const EMPTY_PROGRAM = {
  code: '', installment_count: '', down_payment_pct: '',
  min_premium: '', max_premium: '', fee_pct: '',
  description: '', is_active: true
};

const numOrNull = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
const fmt = (v) => (v == null ? '—' : Number(v).toLocaleString('th-TH'));

function Section({ title, children, error }) {
  return (
    <div className="am-section">
      <h3>{title}</h3>
      {error && <div className="am-error">{error}</div>}
      {children}
    </div>
  );
}

export default function AdminMappings() {
  const { can } = useAuth();
  const canEdit = can('admin-mappings', 'edit');
  const [tab, setTab] = useState('insurers');

  // Insurers state
  const [insurers, setInsurers] = useState([]);
  const [insurerForm, setInsurerForm] = useState({ crm_company_name: '', apfs_insurer_code: '', notes: '' });
  const [insurerErr, setInsurerErr] = useState('');

  // Policy types state
  const [policyTypes, setPolicyTypes] = useState([]);
  const [policyForm, setPolicyForm] = useState({ crm_text: '', apfs_policy_type: 'MOTOR_INSURANCE', notes: '' });
  const [policyErr, setPolicyErr] = useState('');

  // Programs state
  const [programs, setPrograms] = useState([]);
  const [programForm, setProgramForm] = useState(EMPTY_PROGRAM);
  const [programErr, setProgramErr] = useState('');

  const refreshInsurers = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE_URL}/api/mappings/insurers`);
      setInsurers(r.data?.data || []);
    } catch (e) { setInsurerErr(e.message); }
  }, []);

  const refreshPolicyTypes = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE_URL}/api/mappings/policy-types`);
      setPolicyTypes(r.data?.data || []);
    } catch (e) { setPolicyErr(e.message); }
  }, []);

  const refreshPrograms = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE_URL}/api/mappings/programs`);
      setPrograms(r.data?.data || []);
    } catch (e) { setProgramErr(e.message); }
  }, []);

  useEffect(() => {
    refreshInsurers();
    refreshPolicyTypes();
    refreshPrograms();
  }, [refreshInsurers, refreshPolicyTypes, refreshPrograms]);

  // ----- Insurer handlers -----
  const saveInsurer = async () => {
    setInsurerErr('');
    if (!insurerForm.crm_company_name || !insurerForm.apfs_insurer_code) {
      setInsurerErr('CRM company name and APFS insurer code are required.');
      return;
    }
    try {
      await axios.post(`${API_BASE_URL}/api/mappings/insurers`, insurerForm);
      setInsurerForm({ crm_company_name: '', apfs_insurer_code: '', notes: '' });
      refreshInsurers();
    } catch (e) {
      setInsurerErr(e.response?.data?.error || e.message);
    }
  };
  const deleteInsurer = async (id) => {
    if (!window.confirm('Delete this insurer mapping?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/mappings/insurers/${id}`);
      refreshInsurers();
    } catch (e) { setInsurerErr(e.response?.data?.error || e.message); }
  };

  // ----- Policy type handlers -----
  const savePolicyType = async () => {
    setPolicyErr('');
    if (!policyForm.crm_text || !policyForm.apfs_policy_type) {
      setPolicyErr('CRM text and APFS policy type are required.');
      return;
    }
    try {
      await axios.post(`${API_BASE_URL}/api/mappings/policy-types`, policyForm);
      setPolicyForm({ crm_text: '', apfs_policy_type: 'MOTOR_INSURANCE', notes: '' });
      refreshPolicyTypes();
    } catch (e) {
      setPolicyErr(e.response?.data?.error || e.message);
    }
  };
  const deletePolicyType = async (id) => {
    if (!window.confirm('Delete this policy type mapping?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/mappings/policy-types/${id}`);
      refreshPolicyTypes();
    } catch (e) { setPolicyErr(e.response?.data?.error || e.message); }
  };

  // ----- Program handlers -----
  const saveProgram = async () => {
    setProgramErr('');
    if (!programForm.code || !programForm.installment_count) {
      setProgramErr('Code and installment count are required.');
      return;
    }
    try {
      await axios.post(`${API_BASE_URL}/api/mappings/programs`, {
        code: programForm.code.trim(),
        installment_count: Number(programForm.installment_count),
        down_payment_pct: numOrNull(programForm.down_payment_pct),
        min_premium: numOrNull(programForm.min_premium),
        max_premium: numOrNull(programForm.max_premium),
        fee_pct: numOrNull(programForm.fee_pct),
        description: programForm.description,
        is_active: programForm.is_active
      });
      setProgramForm(EMPTY_PROGRAM);
      refreshPrograms();
    } catch (e) {
      setProgramErr(e.response?.data?.error || e.message);
    }
  };

  // Load an existing row into the form so editing never means retyping — the
  // old flow made admins re-enter a program from memory, which is how the
  // premium rules used to get wiped.
  const editProgram = (row) => {
    setProgramErr('');
    setProgramForm({
      code: row.code || '',
      installment_count: row.installment_count ?? '',
      down_payment_pct: row.down_payment_pct ?? '',
      min_premium: row.min_premium ?? '',
      max_premium: row.max_premium ?? '',
      fee_pct: row.fee_pct ?? '',
      description: row.description || '',
      is_active: row.is_active !== false
    });
  };

  // On/off switch. Writes is_active only — premium rules and every submission
  // already carrying this code are untouched.
  const toggleProgramActive = async (row) => {
    const next = !row.is_active;
    const msg = next
      ? `เปิดใช้งานโปรแกรม ${row.code}?\n\nรายการใหม่จะเลือกโปรแกรมนี้ได้อีกครั้ง`
      : `ปิดการใช้งานโปรแกรม ${row.code}?\n\n• รายการที่ส่งไปแล้วไม่ได้รับผลกระทบ — ยังแสดงผลและทำงานต่อได้ตามปกติ\n• เลือกโปรแกรมนี้กับรายการใหม่ไม่ได้อีก\n• เงื่อนไขเบี้ย (min/max/fee) ยังอยู่ครบ เปิดกลับได้ทุกเมื่อ`;
    if (!window.confirm(msg)) return;
    try {
      await axios.patch(`${API_BASE_URL}/api/mappings/programs/${row.id}/active`, { is_active: next });
      refreshPrograms();
    } catch (e) { setProgramErr(e.response?.data?.error || e.message); }
  };

  const deleteProgram = async (id) => {
    if (!window.confirm('ลบโปรแกรมนี้ถาวร?\n\nถ้าแค่ต้องการหยุดใช้กับรายการใหม่ ให้กด "ปิด" แทน — ปลอดภัยกว่าและเปิดกลับได้')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/mappings/programs/${id}`);
      refreshPrograms();
    } catch (e) { setProgramErr(e.response?.data?.error || e.message); }
  };

  return (
    <div className="admin-mappings">
      <div className="am-header">
        <h2>APFS Mapping Tables</h2>
        <p className="am-sub">Configure CRM → APFS field translations. AME pipeline is unaffected.</p>
      </div>

      <div className="am-tabs">
        <button className={tab === 'insurers' ? 'active' : ''} onClick={() => setTab('insurers')}>
          Insurer Codes ({insurers.length})
        </button>
        <button className={tab === 'policyTypes' ? 'active' : ''} onClick={() => setTab('policyTypes')}>
          Policy Types ({policyTypes.length})
        </button>
        <button className={tab === 'programs' ? 'active' : ''} onClick={() => setTab('programs')}>
          Programs ({programs.length})
        </button>
      </div>

      {tab === 'insurers' && (
        <Section title="Insurer Code Mapping" error={insurerErr}>
          <div className="am-form">
            <input
              placeholder="CRM company name (e.g. บริษัท คุ้มภัยโตเกียวมารีน...)"
              value={insurerForm.crm_company_name}
              onChange={(e) => setInsurerForm({ ...insurerForm, crm_company_name: e.target.value })}
            />
            <input
              placeholder="APFS code (e.g. TMA)"
              value={insurerForm.apfs_insurer_code}
              onChange={(e) => setInsurerForm({ ...insurerForm, apfs_insurer_code: e.target.value })}
            />
            <input
              placeholder="Notes (optional)"
              value={insurerForm.notes}
              onChange={(e) => setInsurerForm({ ...insurerForm, notes: e.target.value })}
            />
            {canEdit && <button className="am-btn am-btn-primary" onClick={saveInsurer}>Add / Update</button>}
          </div>
          <table className="am-table">
            <thead>
              <tr><th>CRM Company</th><th>APFS Code</th><th>Notes</th><th></th></tr>
            </thead>
            <tbody>
              {insurers.map((row) => (
                <tr key={row.id}>
                  <td>{row.crm_company_name}</td>
                  <td><code>{row.apfs_insurer_code}</code></td>
                  <td>{row.notes || '—'}</td>
                  <td>
                    {canEdit && <button className="am-btn am-btn-ghost" onClick={() => deleteInsurer(row.id)}>Delete</button>}
                  </td>
                </tr>
              ))}
              {insurers.length === 0 && (
                <tr><td colSpan="4" className="am-empty">No mappings yet.</td></tr>
              )}
            </tbody>
          </table>
        </Section>
      )}

      {tab === 'policyTypes' && (
        <Section title="Policy Type Mapping" error={policyErr}>
          <div className="am-form">
            <input
              placeholder="CRM text (e.g. ประกันภัยรถยนต์ ประเภท 1)"
              value={policyForm.crm_text}
              onChange={(e) => setPolicyForm({ ...policyForm, crm_text: e.target.value })}
            />
            <select
              value={policyForm.apfs_policy_type}
              onChange={(e) => setPolicyForm({ ...policyForm, apfs_policy_type: e.target.value })}
            >
              {APFS_POLICY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <input
              placeholder="Notes (optional)"
              value={policyForm.notes}
              onChange={(e) => setPolicyForm({ ...policyForm, notes: e.target.value })}
            />
            {canEdit && <button className="am-btn am-btn-primary" onClick={savePolicyType}>Add / Update</button>}
          </div>
          <table className="am-table">
            <thead>
              <tr><th>CRM Text</th><th>APFS Type</th><th>Notes</th><th></th></tr>
            </thead>
            <tbody>
              {policyTypes.map((row) => (
                <tr key={row.id}>
                  <td>{row.crm_text}</td>
                  <td><code>{row.apfs_policy_type}</code></td>
                  <td>{row.notes || '—'}</td>
                  <td>
                    {canEdit && <button className="am-btn am-btn-ghost" onClick={() => deletePolicyType(row.id)}>Delete</button>}
                  </td>
                </tr>
              ))}
              {policyTypes.length === 0 && (
                <tr><td colSpan="4" className="am-empty">No mappings yet.</td></tr>
              )}
            </tbody>
          </table>
        </Section>
      )}

      {tab === 'programs' && (
        <Section title="APFS Programs" error={programErr}>
          <p className="am-note">
            ปิดโปรแกรม = เลือกกับ <strong>รายการใหม่</strong> ไม่ได้เท่านั้น
            รายการที่ส่งไปแล้วยังแสดงผลและทำงานต่อได้ตามปกติ และเงื่อนไขเบี้ยยังอยู่ครบ เปิดกลับได้ทุกเมื่อ
          </p>
          <div className="am-form am-form-program">
            <input
              placeholder="Code (APFS-10-down20)"
              value={programForm.code}
              onChange={(e) => setProgramForm({ ...programForm, code: e.target.value })}
            />
            <input
              type="number"
              placeholder="Installments"
              value={programForm.installment_count}
              onChange={(e) => setProgramForm({ ...programForm, installment_count: e.target.value })}
            />
            <input
              type="number"
              step="0.01"
              placeholder="Down %"
              value={programForm.down_payment_pct}
              onChange={(e) => setProgramForm({ ...programForm, down_payment_pct: e.target.value })}
            />
            <input
              type="number"
              step="0.01"
              placeholder="Min premium"
              value={programForm.min_premium}
              onChange={(e) => setProgramForm({ ...programForm, min_premium: e.target.value })}
            />
            <input
              type="number"
              step="0.01"
              placeholder="Max premium"
              value={programForm.max_premium}
              onChange={(e) => setProgramForm({ ...programForm, max_premium: e.target.value })}
            />
            <input
              type="number"
              step="0.01"
              placeholder="Fee %"
              value={programForm.fee_pct}
              onChange={(e) => setProgramForm({ ...programForm, fee_pct: e.target.value })}
            />
            <input
              placeholder="Description"
              value={programForm.description}
              onChange={(e) => setProgramForm({ ...programForm, description: e.target.value })}
            />
            <label className="am-check">
              <input
                type="checkbox"
                checked={programForm.is_active}
                onChange={(e) => setProgramForm({ ...programForm, is_active: e.target.checked })}
              />
              Active
            </label>
            {canEdit && <button className="am-btn am-btn-primary" onClick={saveProgram}>Add / Update</button>}
            {canEdit && programForm.code && (
              <button className="am-btn am-btn-ghost-neutral" onClick={() => setProgramForm(EMPTY_PROGRAM)}>Clear</button>
            )}
          </div>
          <table className="am-table">
            <thead>
              <tr>
                <th>Code</th><th>Installments</th><th>Down %</th>
                <th>Min premium</th><th>Max premium</th><th>Fee %</th>
                <th>Description</th><th>Active</th><th></th>
              </tr>
            </thead>
            <tbody>
              {programs.map((row) => (
                <tr key={row.id} className={row.is_active ? '' : 'am-row-off'}>
                  <td><code>{row.code}</code></td>
                  <td>{row.installment_count}</td>
                  <td>{row.down_payment_pct ?? '—'}</td>
                  <td>{fmt(row.min_premium)}</td>
                  <td>{row.max_premium == null ? '∞' : fmt(row.max_premium)}</td>
                  <td>{row.fee_pct == null ? '—' : `${row.fee_pct}%`}</td>
                  <td>{row.description || '—'}</td>
                  <td>
                    <span className={row.is_active ? 'am-pill am-pill-on' : 'am-pill am-pill-off'}>
                      {row.is_active ? 'เปิด' : 'ปิด'}
                    </span>
                  </td>
                  <td className="am-actions">
                    {canEdit && (
                      <>
                        <button
                          className={row.is_active ? 'am-btn am-btn-warn' : 'am-btn am-btn-ok'}
                          onClick={() => toggleProgramActive(row)}
                        >
                          {row.is_active ? 'ปิด' : 'เปิด'}
                        </button>
                        <button className="am-btn am-btn-ghost-neutral" onClick={() => editProgram(row)}>Edit</button>
                        <button className="am-btn am-btn-ghost" onClick={() => deleteProgram(row.id)}>Delete</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {programs.length === 0 && (
                <tr><td colSpan="9" className="am-empty">No programs yet.</td></tr>
              )}
            </tbody>
          </table>
        </Section>
      )}
    </div>
  );
}
